// ═══════════════════════════════════════════════════════════════════
// CalendrierSejours — vue calendrier mensuelle de l'OCCUPATION par bien
//
// PÉRIMÈTRE VOLONTAIRE — ne pas confondre avec le PLANNING (WeekPlan) :
//   • WeekPlan  = les TÂCHES, par semaine, orienté organisation du travail.
//   • Ce module = les SÉJOURS, par mois, orienté remplissage des logements.
// Ce sont deux vues distinctes, dans deux onglets distincts. Ne pas fusionner.
//
// Biens affichés : uniquement ceux dont la case `calendrierOn` est cochée sur la
// fiche bien (SecPrest.jsx). C'est un choix EXPLICITE et non déduit de `icalUrl` :
//   (a) la liste doit rester stable visuellement même si une URL iCal saute,
//   (b) elle doit survivre à un changement de PMS sans rien reconfigurer.
// Un bien coché reste affiché même sans aucune réservation : une ligne vide est
// une information (problème de tarifs, annonce hors ligne…), pas un vide à masquer.
//
// LECTURE SEULE. L'édition des séjours reste dans SecProp/Facturation.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import { today, feur, prixNuitee } from '../utils.js';
import { M } from './M.jsx';

// Statuts qui ne représentent PAS une occupation réelle du logement.
const ST_ANNULE = ['annule_eviivo', 'annulee'];
const ST_MASQUE = ['ignore']; // écarté volontairement par l'admin — jamais affiché

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const JOURS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

// Conversion 'YYYY-MM-DD' → Date locale à midi (évite tout décalage de fuseau).
const toD = s => (s ? new Date(String(s) + 'T12:00:00') : null);
const nbJours = (a, b) => Math.round((toD(b) - toD(a)) / 864e5);
const fdFR = s => {
  const d = toD(s);
  return d ? d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—';
};

export function CalendrierSejours({ biens = [], sejours = [], clients = [] }) {
  const t = today();
  const [ancre, setAncre] = useState(() => ({ m: toD(t).getMonth(), y: toD(t).getFullYear() }));
  const [sel, setSel] = useState(null);      // séjour affiché dans la modale de détail
  const [annulListe, setAnnulListe] = useState(null); // { bien, liste } pour la pastille annulations

  const { m, y } = ancre;
  const nbJoursMois = new Date(y, m + 1, 0).getDate();
  const jours = useMemo(
    () => Array.from({ length: nbJoursMois }, (_, i) => {
      const d = new Date(y, m, i + 1);
      const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
      return { n: i + 1, iso, jsem: d.getDay(), we: d.getDay() === 0 || d.getDay() === 6 };
    }),
    [y, m, nbJoursMois]
  );
  const premierIso = jours[0]?.iso;
  const dernierIso = jours[jours.length - 1]?.iso;

  const decale = n => setAncre(a => {
    const d = new Date(a.y, a.m + n, 1);
    return { m: d.getMonth(), y: d.getFullYear() };
  });

  const biensAff = useMemo(
    () => (biens || []).filter(b => b && b.calendrierOn === true)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'fr')),
    [biens]
  );

  // Séjours du mois, ventilés par bien.
  // Convention PMS : une barre couvre les NUITÉES (arrivée incluse, départ exclu).
  // Un départ le 9 et une arrivée le 9 ne se chevauchent donc jamais visuellement.
  const parBien = useMemo(() => {
    const map = {};
    for (const b of biensAff) map[b.id] = { barres: [], annules: [], nuits: 0 };
    for (const s of sejours || []) {
      if (!s || !map[s.bid]) continue;
      if (ST_MASQUE.includes(s.statut)) continue;
      const arr = s.dateArrivee, dep = s.dateDepart || s.dateArrivee;
      if (!arr) continue;
      // Chevauchement avec le mois affiché (départ exclu)
      if (dep <= premierIso && arr < premierIso) continue;
      if (arr > dernierIso) continue;
      if (ST_ANNULE.includes(s.statut)) { map[s.bid].annules.push(s); continue; }

      const debIdx = Math.max(0, nbJours(premierIso, arr));
      const finIdx = Math.min(nbJoursMois, nbJours(premierIso, dep)); // exclu
      const span = Math.max(1, finIdx - debIdx);
      if (debIdx >= nbJoursMois || finIdx <= 0) continue;
      map[s.bid].barres.push({
        s, debIdx, span,
        coupeAvant: toD(arr) < toD(premierIso),
        coupeApres: toD(dep) > toD(`${y}-${String(m + 1).padStart(2, '0')}-${nbJoursMois}`),
      });
      map[s.bid].nuits += span;
    }
    return map;
  }, [biensAff, sejours, premierIso, dernierIso, nbJoursMois, y, m]);

  // Jours où un départ et une arrivée tombent sur la même date pour le même bien :
  // c'est la journée où le ménage est critique.
  const enchainements = useMemo(() => {
    const map = {};
    for (const b of biensAff) {
      const actifs = (sejours || []).filter(s => s && s.bid === b.id && !ST_MASQUE.includes(s.statut) && !ST_ANNULE.includes(s.statut));
      const arrivees = new Set(actifs.map(s => s.dateArrivee).filter(Boolean));
      map[b.id] = new Set(actifs.map(s => s.dateDepart).filter(d => d && arrivees.has(d)));
    }
    return map;
  }, [biensAff, sejours]);

  const nomClient = cid => {
    const c = (clients || []).find(x => x.id === cid);
    return c ? (c.name || '—') : '—';
  };

  const LARG_BIEN = 148;
  const estMoisCourant = toD(t).getMonth() === m && toD(t).getFullYear() === y;

  return (<div style={{ paddingBottom: 12 }}>

    {/* ─── Barre de navigation du mois ─── */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
      <button className="btn bgh" onClick={() => decale(-1)} aria-label="Mois précédent">‹</button>
      <div className="D" style={{ fontSize: 19, color: 'var(--or)', minWidth: 168, textAlign: 'center' }}>{MOIS[m]} {y}</div>
      <button className="btn bgh" onClick={() => decale(1)} aria-label="Mois suivant">›</button>
      {!estMoisCourant && (
        <button className="btn bgh" style={{ fontSize: 12 }} onClick={() => setAncre({ m: toD(t).getMonth(), y: toD(t).getFullYear() })}>
          Revenir à aujourd'hui
        </button>
      )}
    </div>

    {biensAff.length === 0 && (
      <div className="card" style={{ padding: 20, fontSize: 13, color: 'var(--si)', lineHeight: 1.55 }}>
        Aucun logement n'est encore suivi dans le calendrier.<br />
        Pour en ajouter un : <strong>Prestations → fiche du bien → « Afficher dans le calendrier des séjours »</strong>.
      </div>
    )}

    {biensAff.length > 0 && (
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <div style={{ minWidth: LARG_BIEN + nbJoursMois * 26 + 92 }}>

          {/* ─── En-tête : numéros de jour ─── */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--li)', position: 'sticky', top: 0, background: 'var(--wh)', zIndex: 2 }}>
            <div style={{ width: LARG_BIEN, flexShrink: 0, padding: '8px 10px', fontSize: 10, fontWeight: 700, color: 'var(--si)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Logement</div>
            <div style={{ flex: 1, display: 'flex' }}>
              {jours.map(j => (
                <div key={j.n} style={{
                  flex: 1, minWidth: 26, textAlign: 'center', padding: '5px 0',
                  background: j.iso === t ? 'var(--cr)' : j.we ? 'rgba(42,37,32,0.035)' : 'transparent',
                  borderLeft: '1px solid var(--li)',
                }}>
                  <div style={{ fontSize: 9, color: 'var(--si)', lineHeight: 1.1 }}>{JOURS[j.jsem]}</div>
                  <div style={{ fontSize: 11, fontWeight: j.iso === t ? 800 : 600, color: j.iso === t ? 'var(--or)' : 'var(--ink)' }}>{j.n}</div>
                </div>
              ))}
            </div>
            <div style={{ width: 92, flexShrink: 0, padding: '8px 10px', fontSize: 10, fontWeight: 700, color: 'var(--si)', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'right', borderLeft: '1px solid var(--li)' }}>Mois</div>
          </div>

          {/* ─── Une ligne par bien ─── */}
          {biensAff.map(b => {
            const d = parBien[b.id] || { barres: [], annules: [], nuits: 0 };
            const tauxPct = Math.round((d.nuits / nbJoursMois) * 100);
            return (
              <div key={b.id} style={{ display: 'flex', borderBottom: '1px solid var(--li)', minHeight: 42 }}>

                <div style={{ width: LARG_BIEN, flexShrink: 0, padding: '8px 10px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.25 }}>{b.name || '—'}</span>
                </div>

                {/* Grille du mois + barres positionnées en absolu */}
                <div style={{ flex: 1, position: 'relative' }}>
                  <div style={{ display: 'flex', height: '100%', position: 'absolute', inset: 0 }}>
                    {jours.map(j => (
                      <div key={j.n} style={{
                        flex: 1, minWidth: 26, borderLeft: '1px solid var(--li)',
                        background: j.iso === t ? 'rgba(255,134,23,0.07)' : j.we ? 'rgba(42,37,32,0.035)' : 'transparent',
                      }}>
                        {enchainements[b.id]?.has(j.iso) && (
                          <div title="Départ et arrivée le même jour — ménage critique" style={{
                            position: 'absolute', top: 2, bottom: 2, left: `${(j.n - 1) / nbJoursMois * 100}%`,
                            width: 3, background: 'var(--dg)', borderRadius: 2, zIndex: 3,
                          }} />
                        )}
                      </div>
                    ))}
                  </div>

                  {d.barres.map(({ s, debIdx, span, coupeAvant, coupeApres }) => {
                    const aCompleter = s.statut === 'a_completer';
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSel(s)}
                        title={`${s.dateArrivee} → ${s.dateDepart || '?'} · ${span} nuit${span > 1 ? 's' : ''}`}
                        style={{
                          position: 'absolute', zIndex: 4, top: 6, bottom: 6,
                          left: `calc(${debIdx / nbJoursMois * 100}% + 2px)`,
                          width: `calc(${span / nbJoursMois * 100}% - 4px)`,
                          background: aCompleter ? 'var(--ye)' : 'var(--mn)',
                          color: aCompleter ? 'var(--ink)' : '#fff',
                          border: 'none', cursor: 'pointer', padding: '0 6px',
                          borderRadius: `${coupeAvant ? '0 ' : '7px '}${coupeApres ? '0 0 ' : '7px 7px '}${coupeAvant ? '0' : '7px'}`,
                          fontSize: 10, fontWeight: 700, fontFamily: 'Montserrat,sans-serif',
                          overflow: 'hidden', whiteSpace: 'nowrap', textAlign: 'left',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                        {aCompleter && <span title="À compléter">⚠️</span>}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {span >= 3 ? `${span} nuits${s.ota ? ' · ' + s.ota : ''}` : span}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Compteur du mois : une ligne à 0 nuit saute aux yeux */}
                <div style={{ width: 92, flexShrink: 0, padding: '6px 10px', borderLeft: '1px solid var(--li)', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: d.nuits === 0 ? 'var(--dg)' : 'var(--ink)' }}>
                    {d.nuits} nuit{d.nuits > 1 ? 's' : ''}
                  </span>
                  <span style={{ fontSize: 9, color: 'var(--si)' }}>{tauxPct}%</span>
                  {d.annules.length > 0 && (
                    <button
                      onClick={() => setAnnulListe({ bien: b, liste: d.annules })}
                      title="Voir les séjours annulés du mois"
                      style={{ border: '1px solid var(--ls)', background: 'transparent', color: 'var(--si)', fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 999, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif' }}>
                      ⃠ {d.annules.length}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {biensAff.length > 0 && (
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 10, color: 'var(--si)', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 16, height: 9, background: 'var(--mn)', borderRadius: 3, display: 'inline-block' }} /> Séjour confirmé</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 16, height: 9, background: 'var(--ye)', borderRadius: 3, display: 'inline-block' }} /> À compléter</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 3, height: 11, background: 'var(--dg)', borderRadius: 2, display: 'inline-block' }} /> Départ + arrivée le même jour</span>
        <span>⃠ Annulations du mois</span>
        <span style={{ marginLeft: 'auto' }}>Une barre couvre les nuitées : le jour du départ est libre pour l'arrivée suivante.</span>
      </div>
    )}

    {/* ─── Détail d'un séjour (lecture seule) ─── */}
    {sel && (
      <M title="Détail du séjour" onC={() => setSel(null)} ch={<div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{(biens.find(b => b.id === sel.bid) || {}).name || '—'}</div>
        <Ligne l="Arrivée" v={fdFR(sel.dateArrivee)} />
        <Ligne l="Départ" v={fdFR(sel.dateDepart)} />
        <Ligne l="Nuitées" v={sel.dateDepart ? `${Math.max(1, nbJours(sel.dateArrivee, sel.dateDepart))}` : '—'} />
        <Ligne l="Voyageurs" v={`${sel.adultes || 0} adulte${(sel.adultes || 0) > 1 ? 's' : ''}${sel.enfants ? ` · ${sel.enfants} enfant${sel.enfants > 1 ? 's' : ''}` : ''}`} />
        <Ligne l="Locataire" v={sel.nomLocataire || '— (non transmis par l\'import)'} />
        <Ligne l="Canal" v={sel.ota || '—'} />
        <Ligne l="Propriétaire" v={nomClient(sel.cid)} />
        <Ligne l="Source" v={sel.source === 'eviivo' ? 'Import automatique (iCal)' : 'Saisie manuelle'} />
        <Ligne l="Statut" v={sel.statut === 'a_completer' ? '⚠️ À compléter' : sel.statut === 'draft' ? 'Brouillon' : (sel.statut || '—')} />
        {/* Même helper que l'aperçu de facturation de la fiche séjour : le chiffre affiché ici
            et celui de SecProp sont toujours identiques au centime. Un tiret signale un séjour
            pas encore renseigné — c'est le signal « il faut aller le compléter ». */}
        <Ligne
          l="Prix / nuitée"
          v={prixNuitee(sel) > 0
            ? `${feur(prixNuitee(sel))} / nuit`
            : '— (séjour non renseigné)'} />
        <div style={{ fontSize: 11, color: 'var(--si)', borderTop: '1px solid var(--li)', paddingTop: 10, lineHeight: 1.5 }}>
          Vue en lecture seule. La modification d'un séjour se fait dans Facturation → Séjours.
        </div>
      </div>} />
    )}

    {/* ─── Liste des annulations du mois pour un bien ─── */}
    {annulListe && (
      <M title={`Annulations — ${annulListe.bien.name || ''}`} onC={() => setAnnulListe(null)} ch={<div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
        <div style={{ fontSize: 11, color: 'var(--si)', marginBottom: 4 }}>
          {MOIS[m]} {y} — ces séjours ont été annulés et n'occupent pas le logement. Ils restent listés ici pour ne pas disparaître silencieusement.
        </div>
        {annulListe.liste.map(s => (
          <button key={s.id} onClick={() => { setSel(s); setAnnulListe(null); }} style={{ textAlign: 'left', border: '1px solid var(--li)', background: 'var(--bg)', borderRadius: 9, padding: '9px 12px', cursor: 'pointer', fontFamily: 'Montserrat,sans-serif', fontSize: 12 }}>
            <strong>{s.dateArrivee} → {s.dateDepart || '?'}</strong>
            <span style={{ color: 'var(--si)' }}>{s.ota ? ` · ${s.ota}` : ''}</span>
          </button>
        ))}
      </div>} />
    )}
  </div>);
}

const Ligne = ({ l, v }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
    <span style={{ fontSize: 11, color: 'var(--si)', textTransform: 'uppercase', letterSpacing: '.05em', minWidth: 104, flexShrink: 0 }}>{l}</span>
    <span style={{ fontWeight: 600 }}>{v}</span>
  </div>
);
